import { useEffect, useReducer, useState } from 'react';

import {
  ActionBar,
  ChakraBox,
  CheckboxV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  MaudCardFrame,
  TextInputV2,
  ToastMessage,
  useToastMessage,
} from '@equip.health/ui';
import { validate as validateEmail } from 'email-validator';
import { isEmpty } from 'lodash';

import { TextInputV2Type } from '~/lib/constants/inquiry.constants';

import MessageModal from '~/components/common/MessageModal';
import { ApiCommand } from '~/lib/Api';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import util from '~/lib/util/util';
import {
  useCreatePatientAppAccountMutation,
  usePutBasicInfoMutation,
} from '~/store/services/patients';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { PATIENT_DATE_OF_BIRTH_REQUIRED } from '~/lib/constants/patients.constants';
import {
  convertPhoneNumberToInternationalFormat,
  getPreferredFirstName,
} from '~/lib/util/patient.util';
import { formatPhoneNumberWithCode } from '~/lib/util/patientContact.util';
import EditEmailWarningModal from '../contact/EditEmailWarningModal';
import ResendEmailLinkActionBar from './ResendEmailLinkActionBar';

const { putBasicInfo: updateBasicInfoUrl } = urlConstants.patients;

const TOP_LEVEL_ID = 'patient-profile__patient-app-setting_edit-mode';
const DUPLICATE_EMAIL_FOUND = 'Duplicate email found.';

const generatePatientProfilePayload = (
  patientBasicDetails: Patient,
): Patient => ({
  addressLine1: patientBasicDetails?.addressLine1,
  addressLine2: patientBasicDetails?.addressLine2,
  affiliationName: patientBasicDetails?.affiliationName,
  chosenName: patientBasicDetails?.chosenName,
  city: patientBasicDetails?.city,
  contactPreferences: patientBasicDetails?.contactPreferences,
  country: patientBasicDetails?.country,
  county: patientBasicDetails?.county,
  dateOfBirth: patientBasicDetails?.dateOfBirth,
  email: patientBasicDetails?.email,
  ethnicities: patientBasicDetails?.ethnicities,
  faxNumber: patientBasicDetails?.faxNumber,
  firstName: patientBasicDetails?.firstName,
  genderOffRecord: patientBasicDetails?.genderOffRecord,
  genderOnRecord: patientBasicDetails?.genderOnRecord,
  hasGivenRecordingConsent: patientBasicDetails?.hasGivenRecordingConsent,
  homePhone: patientBasicDetails?.homePhone,
  languages: patientBasicDetails?.languages,
  lastName: patientBasicDetails?.lastName,
  latitude: patientBasicDetails?.latitude,
  longitude: patientBasicDetails?.longitude,
  mrn: patientBasicDetails?.mrn,
  officeExtension: patientBasicDetails?.officeExtension,
  officeNumber: patientBasicDetails?.officeNumber,
  phoneNumber: patientBasicDetails?.phoneNumber,
  pronounsOffRecord: patientBasicDetails?.pronounsOffRecord,
  pronounsOnRecord: patientBasicDetails?.pronounsOnRecord,
  secondaryCellPhone: patientBasicDetails?.secondaryPhoneNumber,
  sex: patientBasicDetails?.sex,
  state: patientBasicDetails?.state,
  timezone: patientBasicDetails?.timezone,
  userId: patientBasicDetails?.userId,
  zip: patientBasicDetails?.zip,
});

type PatientAppSettingErrors = {
  email?: boolean;
  phoneNumber?: boolean;
};

type PatientAppSettingErrorsAction = {
  field: keyof PatientAppSettingErrors | 'SET';
  error?: boolean;
  newState?: PatientAppSettingErrors;
};

type PatientAppSettingErrorsReducer = (
  state: PatientAppSettingErrors,
  action: PatientAppSettingErrorsAction,
) => PatientAppSettingErrors;

const errorsReducer: PatientAppSettingErrorsReducer = (
  state,
  action,
): PatientAppSettingErrors => {
  const { field, error: fieldError = false } = action;

  return {
    ...state,
    [field]: fieldError,
  };
};

const getCheckBoxText = (hasAuthId: boolean): string =>
  hasAuthId ? 'Grant access to Equip App' : 'Create Equip App account';

const EditPatientAppSettings = ({
  dismissEditMode,
  patientBasicDetails,
  setPatientBasicDetails,
}: {
  dismissEditMode: () => void;
  patientBasicDetails: Patient;
  setPatientBasicDetails: (data: Patient) => void;
}) => {
  const {
    chosenName,
    firstName,
    email,
    hasAuthId,
    isAccountActive,
    hasSetPassword,
    phoneNumber,
    userId: patientId,
    dateOfBirth,
  } = patientBasicDetails ?? {};
  const { createToast } = useToastMessage();

  const [isAccountCheckBoxChecked, setIsAccountCheckBoxChecked] =
    useState<boolean>(false);

  const [isEditWarningModalOpen, setIsEditWarningModalOpen] =
    useState<boolean>(false);

  const [showDuplicateEmailModal, setShowDuplicateEmailModal] =
    useState<boolean>(false);

  const [enteredEmail, setEnteredEmail] = useState<string>(email);
  const [enteredPhoneNumber, setEnteredPhoneNumber] =
    useState<string>(phoneNumber);

  const [isEmailChangedWarningModalOpen, setIsEmailChangedWarningModalOpen] =
    useState<boolean>(false);

  const [errors, dispatchErrors] = useReducer<PatientAppSettingErrorsReducer>(
    errorsReducer,
    {},
  );

  const [putBasicInfo, putBasicInfoResult] = usePutBasicInfoMutation();

  const { loading: isResetBasicInfoLoading, sendRequest: resetBasicInfo } =
    useApi();

  const [createPatientAppAccount, createPatientAppAccountResult] =
    useCreatePatientAppAccountMutation();

  useEffect(() => {
    dispatchErrors({
      error: enteredEmail ? !validateEmail(enteredEmail) : true,
      field: 'email',
    });
  }, [enteredEmail]);

  useEffect(() => {
    dispatchErrors({
      error: !util.validatePhoneNumber(enteredPhoneNumber ?? ''),
      field: 'phoneNumber',
    });
  }, [enteredPhoneNumber]);

  useEffect(() => {
    setIsAccountCheckBoxChecked(hasAuthId ? isAccountActive : false);
  }, [hasAuthId, isAccountActive]);
  const updatePatientBasicDetail = () => {
    const updatedPatientBasicDetails = { ...patientBasicDetails };

    updatedPatientBasicDetails.email = enteredEmail;
    updatedPatientBasicDetails.phoneNumber = enteredPhoneNumber;

    if (hasAuthId) {
      updatedPatientBasicDetails.isAccountActive = isAccountCheckBoxChecked;
    } else {
      updatedPatientBasicDetails.hasAuthId = true;
      updatedPatientBasicDetails.isAccountActive = true;
    }
    setPatientBasicDetails(updatedPatientBasicDetails);
  };

  const onSubmit = () => {
    if (isEmailChangedWarningModalOpen) {
      setIsEmailChangedWarningModalOpen(false);
    }

    const payload = generatePatientProfilePayload(patientBasicDetails);
    payload.email = enteredEmail;
    payload.phoneNumber =
      convertPhoneNumberToInternationalFormat(enteredPhoneNumber);

    if (hasAuthId) {
      payload.isAccountActive = isAccountCheckBoxChecked;
    }

    putBasicInfo({
      patientId,
      patientBody: payload,
    })
      .unwrap()
      .then(() => {
        if (hasAuthId) {
          updatePatientBasicDetail();
          dismissEditMode();
          createToast({
            icon: FAIconName.checkRegular,
            id: `${TOP_LEVEL_ID}-success_toast`,
            text: 'Equip App settings updated',
            variant: 'success',
          });
        } else {
          createPatientAppAccount(patientId);
        }
      })
      .catch((error) => {
        if (
          'data' in error &&
          error.data !== null &&
          error?.data?.toString().includes(DUPLICATE_EMAIL_FOUND)
        ) {
          setShowDuplicateEmailModal(true);
        } else {
          createToast({
            icon: FAIconName.exclamationTriangleRegular,
            id: `${TOP_LEVEL_ID}-api-fail-toast`,
            text: 'Unable to update details',
            variant: 'critical',
          });
        }
      });
  };

  const handleSave = () => {
    if (hasAuthId && email !== enteredEmail && isAccountActive) {
      setIsEmailChangedWarningModalOpen(true);
    } else {
      onSubmit();
    }
  };

  const resetPatientEmailAndPhoneNumber = () => {
    const payload = generatePatientProfilePayload(patientBasicDetails);

    resetBasicInfo({
      command: ApiCommand.PUT,
      options: payload,
      url: updateBasicInfoUrl(patientId),
    });
  };

  useEffect(() => {
    if (createPatientAppAccountResult.isSuccess) {
      updatePatientBasicDetail();
      dismissEditMode();
    } else if (createPatientAppAccountResult.isError) {
      if (
        'data' in createPatientAppAccountResult.error &&
        createPatientAppAccountResult.error.data
          .toString()
          .includes(DUPLICATE_EMAIL_FOUND)
      ) {
        resetPatientEmailAndPhoneNumber();
        setShowDuplicateEmailModal(true);
      } else {
        resetPatientEmailAndPhoneNumber();
      }
    }
  }, [
    createPatientAppAccountResult.isSuccess,
    createPatientAppAccountResult.isError,
  ]);

  const isEmptyDateOfBirth = isEmpty(dateOfBirth);

  const isLoading =
    createPatientAppAccountResult.isLoading ||
    isResetBasicInfoLoading ||
    putBasicInfoResult.isLoading;

  const emailHasChanged = email !== enteredEmail;
  const phoneHasChanged = phoneNumber !== enteredPhoneNumber;
  const accountStatusHasChanged = isAccountActive !== isAccountCheckBoxChecked;
  const isChangingBothEmailAndAccess =
    hasAuthId && accountStatusHasChanged && emailHasChanged;
  // Prevent update if:
  // - patient doesn't have account and we aren't granting access
  // - patient has account and both account status and email are changing
  // - we aren't updating account status or email or phone
  const preventUpdate =
    (!hasAuthId && !isAccountCheckBoxChecked) ||
    isChangingBothEmailAndAccess ||
    !(accountStatusHasChanged || emailHasChanged || phoneHasChanged);

  const isError = errors?.email || errors?.phoneNumber;

  const showResendInvitation = isAccountActive && hasAuthId && !hasSetPassword;

  const isDisableSaveChangeBtn = isError || isLoading || preventUpdate;

  const handleCancelButtonClick = () => {
    if (emailHasChanged || phoneHasChanged || accountStatusHasChanged) {
      setIsEditWarningModalOpen(true);
    } else {
      dismissEditMode();
    }
  };

  return (
    <>
      <EditEmailWarningModal
        id="patient-app-settings"
        isOpen={isEmailChangedWarningModalOpen}
        onClose={() => setIsEmailChangedWarningModalOpen(false)}
        onConfirm={onSubmit}
      />
      <MessageModal
        dismissVariant="accent.critical"
        id="patient-app-settings__duplicate-email"
        isOpen={showDuplicateEmailModal}
        onCancel={() => {
          setEnteredEmail(email);
          setShowDuplicateEmailModal(false);
        }}
        subTitle={`An account using ${enteredEmail} already exists. A unique email address is required to create an account.`}
        title="Error creating patient"
      />
      <DiscardChangesModalV2
        id={`${TOP_LEVEL_ID}__edit-close-warning`}
        isOpen={isEditWarningModalOpen}
        onClose={() => setIsEditWarningModalOpen(false)}
        onConfirm={dismissEditMode}
      />
      <ChakraBox marginTop="24px">
        <MaudCardFrame id={TOP_LEVEL_ID}>
          <ChakraBox margin="3px 5px 24px 5px">
            <CheckboxV2
              id="check-box"
              isChecked={isAccountCheckBoxChecked}
              isDisabled={isEmptyDateOfBirth}
              label={getCheckBoxText(hasAuthId)}
              onChange={(event: boolean) => setIsAccountCheckBoxChecked(event)}
            />
            {isEmptyDateOfBirth && (
              <ChakraBox marginTop="24px">
                <ToastMessage
                  icon={FAIconName.exclamationTriangleRegular}
                  id={`${TOP_LEVEL_ID}__date-of-birth-required-warning`}
                  isFullWidth
                  text={PATIENT_DATE_OF_BIRTH_REQUIRED}
                  variant="critical"
                />
              </ChakraBox>
            )}
            {(hasAuthId || isAccountCheckBoxChecked) && (
              <ChakraBox display="flex" gap="24px" marginTop="12px">
                <ChakraBox width="345px">
                  <InputGroupV2
                    id={`${TOP_LEVEL_ID}__email-group`}
                    isRequired
                    label="Email"
                    shouldShowAsteriskWhenRequired
                    value={errors?.email && 'Please enter a valid email'}
                  >
                    <TextInputV2
                      id={`${TOP_LEVEL_ID}_email-input`}
                      isInvalid={errors?.email}
                      leftIcon={FAIconName.envelopeRegular}
                      onChange={(event: string) => {
                        setEnteredEmail(event);
                      }}
                      type={TextInputV2Type.EMAIL}
                      value={enteredEmail}
                    />
                  </InputGroupV2>
                </ChakraBox>
                <ChakraBox width="345px">
                  <InputGroupV2
                    id={`${TOP_LEVEL_ID}__phone-group`}
                    isRequired
                    label="Mobile Phone"
                    shouldShowAsteriskWhenRequired
                    value={
                      errors?.phoneNumber && 'Please enter a valid phone number'
                    }
                  >
                    <TextInputV2
                      id={`${TOP_LEVEL_ID}_phone-input`}
                      isDisabled={hasAuthId && !isAccountCheckBoxChecked}
                      isInvalid={errors?.phoneNumber}
                      leftIcon={FAIconName.mobileRegular}
                      onChange={(value) => {
                        setEnteredPhoneNumber(value || '');
                      }}
                      type={TextInputV2Type.PHONE_CODE}
                      value={formatPhoneNumberWithCode(enteredPhoneNumber)}
                    />
                  </InputGroupV2>
                </ChakraBox>
              </ChakraBox>
            )}
          </ChakraBox>
          {isChangingBothEmailAndAccess && (
            <ChakraBox marginBottom="24px">
              <ActionBar
                barIcon={FAIconName.exclamationTriangleRegular}
                buttonText="Resend password creation email"
                id={`${TOP_LEVEL_ID}__error-appaccess-along-with-email`}
                size="medium"
                text="Equip App access cannot be changed while also changing the user’s email address"
                variant="critical"
              />
            </ChakraBox>
          )}
          <ChakraBox display="flex" justifyContent="space-between">
            <FireflyButton
              id="btn"
              isDisabled={isLoading}
              leftIcon={FAIconName.XmarkRegular}
              onClick={handleCancelButtonClick}
              variant="secondary"
            >
              Cancel
            </FireflyButton>
            <FireflyButton
              id="btn"
              isDisabled={isDisableSaveChangeBtn}
              isLoading={isLoading}
              leftIcon={FAIconName.checkRegular}
              onClick={handleSave}
            >
              Save Changes
            </FireflyButton>
          </ChakraBox>
          <ResendEmailLinkActionBar
            id={`${TOP_LEVEL_ID}__view-resend-confirmation-mail`}
            patientId={patientId}
            patientName={getPreferredFirstName(chosenName, firstName, false)}
            showResendInvitation={showResendInvitation}
          />
        </MaudCardFrame>
      </ChakraBox>
    </>
  );
};

export default EditPatientAppSettings;
